import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Inbox, Filter, RefreshCw, Phone, MessageCircle, Mail, User, Briefcase,
  AlertTriangle, Calendar, Clock, ExternalLink, CheckCircle2, TrendingDown,
  Timer, DollarSign, MoreHorizontal, ChevronRight,
} from "lucide-react";

const STALENESS_STYLE = {
  fresh:  "bg-emerald-50 text-emerald-800 border-emerald-200",
  green:  "bg-emerald-50 text-emerald-800 border-emerald-200",
  yellow: "bg-amber-50 text-amber-800 border-amber-200",
  orange: "bg-orange-50 text-orange-800 border-orange-200",
  red:    "bg-red-50 text-red-800 border-red-200",
  never:  "bg-neutral-50 text-neutral-600 border-neutral-200",
};

const PRIORITY_ORDER = { urgent: 4, high: 3, normal: 2, low: 1 };
const PRIORITY_STYLE = {
  urgent: "bg-red-100 text-red-800",
  high:   "bg-amber-100 text-amber-800",
  normal: "bg-blue-100 text-blue-800",
  low:    "bg-neutral-100 text-neutral-600",
};
const PRIORITY_LABEL = { urgent: "URGENTE", high: "Alta", normal: "Normal", low: "Baja" };

const TYPE_ICON = {
  call: Phone, whatsapp: MessageCircle, email: Mail,
  followup: RefreshCw, docs: Briefcase, sale: DollarSign, custom: MoreHorizontal,
};
const TYPE_LABEL = {
  call: "Llamar", whatsapp: "WhatsApp", email: "Email",
  followup: "Seguimiento", docs: "Docs", sale: "Venta", custom: "Custom",
};

/**
 * V1.30.1 · ANA · Bandeja de trabajo diaria.
 *
 * Vista optimizada para que Ana abra por la mañana y sepa exactamente:
 *   - Vencidas · Hoy · Próximos 7 días · Sin fecha
 *   - Oportunidades sin movimiento
 *   - KPIs del pipeline (abierto + cobrado este mes)
 * Cada tarea muestra alumno + teléfono + prioridad + fecha + acciones
 * rápidas (Llamar, WhatsApp, Abrir ficha, Posponer +1h/+1d/+1sem, Hecha).
 */
export default function AnaInboxPanel({ onOpenStudent }) {
  const [scope, setScope] = useState("mine");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/ana/inbox?scope=${scope}`);
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setLoading(false); }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const complete = async (task) => {
    try {
      await api.post(`/ana/tasks/${task.id}/complete`);
      toast.success("Tarea completada");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const postpone = async (task, delta) => {
    try {
      const { data } = await api.post(`/ana/tasks/${task.id}/postpone`, { delta });
      toast.success(`Pospuesta a ${new Date(data.due_at).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}`);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const c = data?.counters || {};

  return (
    <div className="space-y-4" data-testid="ana-inbox-panel">
      {/* Header + selector scope */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold text-neutral-900 flex items-center gap-2">
            <Inbox className="h-5 w-5" /> Bandeja ANA
          </h2>
          <p className="text-xs text-neutral-500">Tu trabajo comercial del día en una sola pantalla.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-neutral-300 p-0.5">
            <button
              type="button"
              onClick={() => setScope("mine")}
              data-testid="ana-inbox-scope-mine"
              className={"text-xs font-semibold px-3 py-1 rounded " + (scope === "mine" ? "bg-orange-500 text-white" : "text-neutral-600 hover:text-neutral-900")}
            >
              👩‍💼 Mis tareas
            </button>
            <button
              type="button"
              onClick={() => setScope("all")}
              data-testid="ana-inbox-scope-all"
              className={"text-xs font-semibold px-3 py-1 rounded " + (scope === "all" ? "bg-orange-500 text-white" : "text-neutral-600 hover:text-neutral-900")}
            >
              Todas
            </button>
          </div>
          <button onClick={load} className="text-xs font-semibold text-neutral-600 hover:text-neutral-900 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-neutral-100" data-testid="ana-inbox-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Actualizar
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="ana-inbox-kpis">
        <Kpi icon={AlertTriangle} label="Vencidas" value={c.overdue || 0} color="red" testid="kpi-overdue" />
        <Kpi icon={Phone} label="Llamadas" value={c.calls || 0} color="blue" testid="kpi-calls" />
        <Kpi icon={MessageCircle} label="WhatsApp" value={c.whatsapps || 0} color="emerald" testid="kpi-whatsapps" />
        <Kpi icon={Mail} label="Emails" value={c.emails || 0} color="purple" testid="kpi-emails" />
        <Kpi icon={RefreshCw} label="Seguimientos" value={c.followups || 0} color="amber" testid="kpi-followups" />
        <Kpi icon={Briefcase} label="Oport. abiertas" value={c.open_opportunities || 0} secondary={`${(c.pipeline_value_eur || 0).toLocaleString("es-ES")} €`} color="sky" testid="kpi-opps" />
        <Kpi icon={DollarSign} label="Cobrado mes" value={`${(c.month_closed_eur || 0).toLocaleString("es-ES")} €`} color="lime" testid="kpi-closed" />
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500 py-8 text-center">Cargando bandeja…</div>
      ) : (
        <>
          <TaskGroup
            title="⚠️ Vencidas"
            testId="ana-inbox-overdue"
            tasks={data?.overdue || []}
            emptyText="Ninguna tarea vencida. ¡Buen trabajo!"
            variant="danger"
            onComplete={complete}
            onPostpone={postpone}
            onOpen={onOpenStudent}
          />
          <TaskGroup
            title="🔴 Hoy"
            testId="ana-inbox-today"
            tasks={data?.today || []}
            emptyText="Sin tareas para hoy."
            variant="today"
            onComplete={complete}
            onPostpone={postpone}
            onOpen={onOpenStudent}
          />
          <TaskGroup
            title="🟡 Próximos 7 días"
            testId="ana-inbox-upcoming"
            tasks={data?.upcoming || []}
            emptyText="Sin tareas próximas."
            variant="upcoming"
            onComplete={complete}
            onPostpone={postpone}
            onOpen={onOpenStudent}
            collapsedByDefault
          />
          {(data?.no_date || []).length > 0 && (
            <TaskGroup
              title="⌛ Sin fecha"
              testId="ana-inbox-no-date"
              tasks={data.no_date}
              emptyText=""
              variant="neutral"
              onComplete={complete}
              onPostpone={postpone}
              onOpen={onOpenStudent}
              collapsedByDefault
            />
          )}

          {(data?.stale_opportunities || []).length > 0 && (
            <StaleOpportunitiesGroup
              testId="ana-inbox-stale-opps"
              opportunities={data.stale_opportunities}
              onOpen={onOpenStudent}
            />
          )}
        </>
      )}
    </div>
  );
}


function Kpi({ icon: Icon, label, value, secondary, color, testid }) {
  const colorMap = {
    red: "bg-red-50 text-red-800 border-red-200",
    blue: "bg-blue-50 text-blue-800 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    purple: "bg-purple-50 text-purple-800 border-purple-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    sky: "bg-sky-50 text-sky-800 border-sky-200",
    lime: "bg-lime-50 text-lime-800 border-lime-200",
  };
  return (
    <div data-testid={testid} className={"rounded-lg border p-2.5 " + (colorMap[color] || colorMap.blue)}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase font-semibold opacity-80">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="text-lg font-bold mt-0.5">{value}</div>
      {secondary && <div className="text-[10px] opacity-70">{secondary}</div>}
    </div>
  );
}


function TaskGroup({ title, testId, tasks, emptyText, variant, onComplete, onPostpone, onOpen, collapsedByDefault = false }) {
  const [collapsed, setCollapsed] = useState(collapsedByDefault);
  const sorted = useMemo(() => [...tasks].sort((a, b) => {
    const pa = PRIORITY_ORDER[a.priority] || 0;
    const pb = PRIORITY_ORDER[b.priority] || 0;
    if (pa !== pb) return pb - pa;
    return (a.due_at || "").localeCompare(b.due_at || "");
  }), [tasks]);

  const borderCls = variant === "danger" ? "border-red-300" : variant === "today" ? "border-orange-300" : "border-neutral-200";
  return (
    <div data-testid={testId} className={"rounded-lg border-2 " + borderCls + " bg-white overflow-hidden"}>
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 bg-neutral-50 hover:bg-neutral-100"
      >
        <div className="font-semibold text-sm">{title} <span className="text-neutral-500 font-normal">({sorted.length})</span></div>
        <ChevronRight className={"h-4 w-4 text-neutral-400 transition-transform " + (collapsed ? "" : "rotate-90")} />
      </button>
      {!collapsed && (
        <div className="divide-y divide-neutral-100">
          {sorted.length === 0 ? (
            <div className="text-xs text-neutral-500 px-3 py-4 italic">{emptyText}</div>
          ) : sorted.map((t) => (
            <TaskRow key={t.id} task={t} onComplete={onComplete} onPostpone={onPostpone} onOpen={onOpen} />
          ))}
        </div>
      )}
    </div>
  );
}


function TaskRow({ task, onComplete, onPostpone, onOpen }) {
  const Icon = TYPE_ICON[task.type] || MoreHorizontal;
  const student = task.student || {};
  const staleness = task.staleness || {};
  const phone = student.phone || "";
  const waPhone = phone.replace(/[^0-9]/g, "");
  const waFmt = waPhone.startsWith("34") ? waPhone : (waPhone.length === 9 ? "34" + waPhone : waPhone);
  const waUrl = waFmt ? `https://wa.me/${waFmt}` : null;
  const telUrl = phone ? `tel:${phone}` : null;

  return (
    <div className="px-3 py-2 hover:bg-neutral-50" data-testid={`ana-inbox-task-${task.id}`}>
      <div className="flex items-center gap-2">
        <span className={"text-[9px] font-bold rounded-full px-1.5 py-0.5 flex-shrink-0 " + PRIORITY_STYLE[task.priority]}>{PRIORITY_LABEL[task.priority]}</span>
        <div className="flex-shrink-0 w-14 text-[10px] text-neutral-500 font-mono">
          {task.due_at ? new Date(task.due_at).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
        </div>
        <Icon className="h-3.5 w-3.5 text-neutral-500 flex-shrink-0" />
        <span className="text-xs font-medium text-neutral-700 flex-shrink-0">{TYPE_LABEL[task.type]}</span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-neutral-800 truncate">{task.title}</div>
          <div className="text-[10px] text-neutral-500 flex items-center gap-1.5 flex-wrap">
            {student.name && <span className="font-medium">{student.name}</span>}
            {phone && <span>· {phone}</span>}
            {staleness.label && <span className={"rounded-full border px-1 py-0 " + (STALENESS_STYLE[staleness.bucket] || STALENESS_STYLE.never)}>{staleness.label}</span>}
          </div>
        </div>

        {/* Acciones rápidas */}
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {telUrl && (
            <a href={telUrl} data-testid={`ana-inbox-call-${task.id}`} className="text-blue-700 hover:bg-blue-50 rounded p-1" title="Llamar">
              <Phone className="h-3.5 w-3.5" />
            </a>
          )}
          {waUrl && (
            <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid={`ana-inbox-wa-${task.id}`} className="text-emerald-700 hover:bg-emerald-50 rounded p-1" title="WhatsApp">
              <MessageCircle className="h-3.5 w-3.5" />
            </a>
          )}
          {student.id && (
            <button
              onClick={() => onOpen && onOpen(student.id)}
              data-testid={`ana-inbox-open-${task.id}`}
              className="text-neutral-600 hover:bg-neutral-100 rounded p-1"
              title="Abrir ficha alumno"
            >
              <User className="h-3.5 w-3.5" />
            </button>
          )}
          <div className="mx-1 h-4 w-px bg-neutral-200" />
          <button onClick={() => onPostpone(task, "1h")} data-testid={`ana-inbox-postpone-1h-${task.id}`} className="text-[10px] font-semibold text-neutral-600 hover:bg-neutral-100 rounded px-1 py-0.5" title="+1 hora">+1h</button>
          <button onClick={() => onPostpone(task, "1d")} data-testid={`ana-inbox-postpone-1d-${task.id}`} className="text-[10px] font-semibold text-neutral-600 hover:bg-neutral-100 rounded px-1 py-0.5" title="+1 día">+1d</button>
          <button onClick={() => onPostpone(task, "1w")} data-testid={`ana-inbox-postpone-1w-${task.id}`} className="text-[10px] font-semibold text-neutral-600 hover:bg-neutral-100 rounded px-1 py-0.5" title="+1 semana">+1sem</button>
          <button onClick={() => onComplete(task)} data-testid={`ana-inbox-complete-${task.id}`} className="text-emerald-700 hover:bg-emerald-50 rounded p-1 ml-1" title="Marcar como hecha">
            <CheckCircle2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      {task.notes && <div className="text-[11px] text-neutral-600 mt-1 pl-16">{task.notes}</div>}
    </div>
  );
}


function StaleOpportunitiesGroup({ testId, opportunities, onOpen }) {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <div data-testid={testId} className="rounded-lg border-2 border-purple-300 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 bg-purple-50 hover:bg-purple-100"
      >
        <div className="font-semibold text-sm flex items-center gap-2">
          <Timer className="h-4 w-4 text-purple-600" />
          Oportunidades sin movimiento ({opportunities.length})
        </div>
        <ChevronRight className={"h-4 w-4 text-neutral-400 transition-transform " + (collapsed ? "" : "rotate-90")} />
      </button>
      {!collapsed && (
        <div className="divide-y divide-neutral-100">
          {opportunities.map((o) => {
            const daysStale = o.stage_updated_at
              ? Math.floor((Date.now() - new Date(o.stage_updated_at).getTime()) / (1000 * 60 * 60 * 24))
              : null;
            return (
              <div key={o.id} className="px-3 py-2 hover:bg-neutral-50" data-testid={`ana-inbox-stale-opp-${o.id}`}>
                <div className="flex items-center gap-2">
                  <TrendingDown className="h-3.5 w-3.5 text-purple-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{o.title}</div>
                    <div className="text-[10px] text-neutral-500 flex items-center gap-1.5">
                      <span>{o.student?.name || "—"}</span>
                      <span>·</span>
                      <span className="uppercase font-mono">{o.stage}</span>
                      <span>·</span>
                      <span className="font-semibold">{(o.amount || 0).toLocaleString("es-ES")} €</span>
                      {daysStale !== null && <><span>·</span><span className="text-purple-700 font-semibold">{daysStale}d parada</span></>}
                    </div>
                  </div>
                  {o.student?.id && (
                    <button onClick={() => onOpen && onOpen(o.student.id)} className="text-neutral-600 hover:bg-neutral-100 rounded p-1" title="Abrir alumno">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
